import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";

interface CurrencyContextType {
  currency: string;
  setCurrency: (c: string) => void;
  rate: number; // rate from USD to selected currency
  symbol: string;
  formatPrice: (usd: number) => string;
  loading: boolean;
  currencies: { code: string; name: string; symbol: string }[];
}

const POPULAR_CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "CAD", name: "Canadian Dollar", symbol: "CA$" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CHF", name: "Swiss Franc", symbol: "CHF" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "MXN", name: "Mexican Peso", symbol: "MX$" },
  { code: "BRL", name: "Brazilian Real", symbol: "R$" },
  { code: "KRW", name: "South Korean Won", symbol: "₩" },
  { code: "SEK", name: "Swedish Krona", symbol: "kr" },
  { code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
  { code: "SGD", name: "Singapore Dollar", symbol: "S$" },
  { code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
  { code: "THB", name: "Thai Baht", symbol: "฿" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
  { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
  { code: "PLN", name: "Polish Złoty", symbol: "zł" },
];

const LOCALE_TO_CURRENCY: Record<string, string> = {
  en_US: "USD", en_GB: "GBP", en_AU: "AUD", en_CA: "CAD", en_NZ: "NZD", en_SG: "SGD", en_HK: "HKD", en_ZA: "ZAR", en_IN: "INR",
  fr: "EUR", de: "EUR", it: "EUR", es: "EUR", nl: "EUR", pt_BR: "BRL", pt: "EUR",
  ja: "JPY", zh: "CNY", ko: "KRW", th: "THB", hi: "INR", sv: "SEK", pl: "PLN", ar: "AED",
};

function detectCurrency(): string {
  try {
    const saved = localStorage.getItem("preferred_currency");
    if (saved) return saved;

    const locale = navigator.language || "en-US";
    const normalized = locale.replace("-", "_");
    // Try exact match, then language only
    return LOCALE_TO_CURRENCY[normalized] || LOCALE_TO_CURRENCY[normalized.split("_")[0]] || "USD";
  } catch {
    return "USD";
  }
}

const CurrencyContext = createContext<CurrencyContextType | null>(null);

let rateCache: Record<string, { rate: number; ts: number }> = {};
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState(detectCurrency);
  const [rate, setRate] = useState(1);
  const [loading, setLoading] = useState(false);

  const setCurrency = useCallback((c: string) => {
    setCurrencyState(c);
    try { localStorage.setItem("preferred_currency", c); } catch {}
  }, []);

  useEffect(() => {
    if (currency === "USD") {
      setRate(1);
      return;
    }

    const cached = rateCache[currency];
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setRate(cached.rate);
      return;
    }

    setLoading(true);
    fetch(`https://open.er-api.com/v6/latest/USD`)
      .then((r) => r.json())
      .then((data) => {
        if (data.rates?.[currency]) {
          const r = data.rates[currency];
          rateCache[currency] = { rate: r, ts: Date.now() };
          setRate(r);
        }
      })
      .catch(() => {
        // fallback: keep current rate
      })
      .finally(() => setLoading(false));
  }, [currency]);

  const info = POPULAR_CURRENCIES.find((c) => c.code === currency) || { symbol: currency };

  const formatPrice = useCallback(
    (usd: number) => {
      const converted = Math.round(usd * rate);
      // Use Intl for proper formatting
      try {
        return new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          maximumFractionDigits: 0,
        }).format(converted);
      } catch {
        return `${info.symbol}${converted.toLocaleString()}`;
      }
    },
    [rate, currency, info.symbol]
  );

  return (
    <CurrencyContext.Provider
      value={{
        currency,
        setCurrency,
        rate,
        symbol: info.symbol,
        formatPrice,
        loading,
        currencies: POPULAR_CURRENCIES,
      }}
    >
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within CurrencyProvider");
  return ctx;
}
